import { Box, Text, VStack } from '@chakra-ui/react'
import { useApp } from '@hb/react'
import {
  computeMdmComplexity,
  FieldTypes,
  generateMdmSummary,
  mdmDataReviewedField,
  MdmDataReviewedItem,
  MdmProblemsAddressed,
  mdmProblemsAddressedFieldAdmin,
  mdmProblemsAddressedFieldProvider,
  MdmRisk,
  mdmRiskOfManagementField,
  TextAreaField,
} from '@hb/shared'
import { useEffect, useMemo } from 'react'
import { useField, useForm } from 'react-final-form'
import { FormElement } from '../../../../components/forms/Input'
import TextArea from '../../../../components/forms/Input/Text/TextArea/TextArea'
import { SectionLabel } from '../../SectionLabel'

// Stable field schema (module-level so the reference never changes between renders).
const summaryField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Clinical summary...',
  optional: true,
  withDotPhrases: true,
  minRows: 2,
}

export const MdmInput = () => {
  const form = useForm()
  const { appName } = useApp()
  const mdmProblemsAddressedField = useMemo(
    () => (appName === 'app' ? mdmProblemsAddressedFieldAdmin : mdmProblemsAddressedFieldProvider),
    [appName],
  )
  const {
    input: { value: problems },
  } = useField<MdmProblemsAddressed | undefined>('data.mdmProblemsAddressed', {
    subscription: { value: true },
  })
  const {
    input: { value: dataReviewed },
  } = useField<MdmDataReviewedItem[] | undefined>('data.mdmDataReviewed', {
    subscription: { value: true },
  })
  const {
    input: { value: risk },
  } = useField<MdmRisk | undefined>('data.mdmRiskOfManagement', {
    subscription: { value: true },
  })
  const { input: summaryInput, meta: summaryMeta } = useField<string>('data.mdmSummary', {
    subscription: { value: true, active: true },
  })
  const summary = summaryInput.value

  useEffect(() => {
    form.change('data.mdmComplexity', computeMdmComplexity(problems, dataReviewed, risk))
    const generated = generateMdmSummary(problems, dataReviewed, risk, appName)
    if (generated) form.change('data.mdmSummary', generated)
  }, [problems, dataReviewed, risk, appName, form])

  return (
    <VStack align="stretch" spacing={1} w="100%" px={5} py="14px">
      <SectionLabel>Medical Decision Making</SectionLabel>
      <Box>
        <Text fontSize="xs" fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={1}>
          Describe the problems you addressed and the decisions you made today.
        </Text>
        <FormElement
          root
          showName={false}
          field={{
            name: 'MDM Problems',
            children: { mdmProblemsAddressed: mdmProblemsAddressedField },
          }}
          name="data"
        />
      </Box>
      <Box>
        <Text fontSize="xs" fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={1}>
          Data reviewed and analyzed (select all that apply)
        </Text>
        <FormElement
          root
          showName={false}
          field={{ name: 'MDM Data', children: { mdmDataReviewed: mdmDataReviewedField } }}
          name="data"
        />
      </Box>
      <Box>
        <Text fontSize="xs" fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={1}>
          Document the risk level of your management decisions today.
        </Text>
        <FormElement
          root
          showName={false}
          field={{ name: 'MDM Risk', children: { mdmRiskOfManagement: mdmRiskOfManagementField } }}
          name="data"
        />
      </Box>
      {summary ? (
        <Box>
          <Text
            fontSize="xs"
            fontWeight={600}
            color="gray.500"
            _dark={{ color: 'gray.400' }}
            mb={1}>
            Clinical summary
          </Text>
          {/* Shared dot-phrase TextArea so '.'/Tab template expansion works in the summary field. */}
          <TextArea field={summaryField} input={summaryInput} meta={summaryMeta} />
        </Box>
      ) : null}
    </VStack>
  )
}
